import React, { useContext, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import CampaignModal from "../../components/CampaignModal";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Box, Typography } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "16px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  backButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
    color: "#3b82f6",
    fontSize: "28px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  contentWrapper: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "16px",
    alignItems: "start",
  },
  formColumn: {
    background: "white",
    borderRadius: "8px",
    padding: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    gridColumn: "1 / 3",
    overflow: "hidden",
  },
  previewColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    position: "sticky",
    top: "24px",
  },
  phoneFrame: {
    background: "#000",
    borderRadius: "40px",
    padding: "10px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    maxWidth: "300px",
    margin: "0 auto",
  },
  phoneScreen: {
    background: "#fff",
    borderRadius: "35px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "550px",
    width: "260px",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.1)",
  },
  phoneHeader: {
    background: "#075e54",
    color: "white",
    padding: "12px 16px",
    fontSize: "14px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  phoneMessages: {
    flex: 1,
    overflowY: "auto",
    padding: "12px",
    background: "#ece5dd",
    backgroundImage: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"100\" height=\"100\"><defs><pattern id=\"pattern\" x=\"0\" y=\"0\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"50\" cy=\"50\" r=\"1\" fill=\"rgba(0,0,0,0.03)\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"%23ece5dd\"/><rect width=\"100\" height=\"100\" fill=\"url(%23pattern)\"/></svg>')",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minHeight: 0,
  },
  messageBubble: {
    maxWidth: "85%",
    padding: "8px 12px",
    borderRadius: "18px",
    fontSize: "13px",
    lineHeight: "1.4",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    animation: "$fadeIn 0.3s ease-in",
    flexShrink: 0,
  },
  messageBubbleReceived: {
    background: "#fff",
    color: "#000",
    alignSelf: "flex-start",
    borderBottomLeftRadius: "4px",
    boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
  },
  emptyPhonePreview: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#999",
    fontSize: "13px",
    textAlign: "center",
    padding: "20px",
  },
  warningBanner: {
    background: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "8px",
    padding: "8px 10px",
    marginBottom: "8px",
    fontSize: "11px",
    color: "#856404",
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    flexShrink: 0,
  },
  warningIcon: {
    fontSize: "14px",
    flexShrink: 0,
    marginTop: "1px",
  },
  warningText: {
    textAlign: "left",
    lineHeight: "1.3",
  },
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
      transform: "translateY(10px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  "@media (max-width: 1400px)": {
    contentWrapper: {
      gridTemplateColumns: "1fr",
    },
    formColumn: {
      gridColumn: "1",
    },
  },
}));

const CampaignConfig = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [previewMessages, setPreviewMessages] = useState({
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
  });

  const queryParams = new URLSearchParams(location.search);
  const templateType = queryParams.get("templateType");

  const getTemplateConfig = () => {
    const templates = {
      humanizado: {
        apiType: "humanized",
        maxContacts: 40,
        delayBetweenContacts: 5000,
        intervalBetweenMessages: 2000,
        humanized: true,
      },
      bot: {
        apiType: "medium",
        maxContacts: 70,
        delayBetweenContacts: 2000,
        intervalBetweenMessages: 500,
        humanized: false,
      },
      forcado: {
        apiType: "high",
        maxContacts: 110,
        delayBetweenContacts: 500,
        intervalBetweenMessages: 100,
        humanized: false,
      },
      velocidade: {
        apiType: "uzap-medium",
        maxContacts: 200,
        delayBetweenContacts: 1000,
        intervalBetweenMessages: 300,
        humanized: false,
      },
      crescimento: {
        apiType: "official-notification-hub",
        maxContacts: null,
        delayBetweenContacts: 3000,
        intervalBetweenMessages: 1000,
        humanized: false,
      },
      seguranca: {
        apiType: "official-waba",
        maxContacts: null,
        delayBetweenContacts: 2000,
        intervalBetweenMessages: 500,
        humanized: false,
      },
    };
    return templates[templateType] || templates.humanizado;
  };

  const templateConfig = getTemplateConfig();

  const handlePreviewUpdate = useCallback((messages) => {
    setPreviewMessages(messages);
  }, []);

  const handleBackClick = () => {
    try {
      history.push("/templates");
    } catch (err) {
      console.error('Erro ao voltar:', err);
      history.push("/campaigns");
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <button
          className={classes.backButton}
          onClick={handleBackClick}
        >
          <ArrowBackIcon />
        </button>
        <h1 className={classes.title}>Nova Campanha</h1>
      </div>

      <div className={classes.contentWrapper}>
        <div className={classes.formColumn}>
          {templateConfig && (
            <Box mb={2} p={2} style={{
              background: "#f0f9ff",
              border: "1px solid #bfdbfe",
              borderRadius: "8px",
            }}>
              <Typography style={{ fontSize: "13px", fontWeight: 700, color: "#0c4a6e", marginBottom: "8px" }}>
                ⚙️ Configuração do Template (Editável)
              </Typography>
              <Typography style={{ fontSize: "11px", color: "#0c4a6e", marginBottom: "12px" }}>
                Você pode editar esses parâmetros abaixo no formulário
              </Typography>
              <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "12px" }}>
                {templateConfig.maxContacts && (
                  <Box>
                    <strong>Máx. Contatos:</strong> {templateConfig.maxContacts}
                  </Box>
                )}
                {templateConfig.delayBetweenContacts && (
                  <Box>
                    <strong>Delay entre Contatos:</strong> {templateConfig.delayBetweenContacts}ms
                  </Box>
                )}
                {templateConfig.intervalBetweenMessages && (
                  <Box>
                    <strong>Intervalo entre Mensagens:</strong> {templateConfig.intervalBetweenMessages}ms
                  </Box>
                )}
                {templateConfig.apiType && (
                  <Box>
                    <strong>API Type:</strong> {templateConfig.apiType}
                  </Box>
                )}
              </Box>
            </Box>
          )}
          <CampaignModal
            open={true}
            onClose={() => handleBackClick()}
            templateConfig={templateConfig}
            resetPagination={() => history.push("/campaigns")}
            isPage={true}
            onPreviewUpdate={handlePreviewUpdate}
          />
        </div>

        <div className={classes.previewColumn}>
          <div className={classes.phoneFrame}>
            <div className={classes.phoneScreen}>
              <div className={classes.phoneHeader}>
                <span>💬 Prévia</span>
                <span style={{ fontSize: "12px" }}>⋮</span>
              </div>
              <div className={classes.phoneMessages}>
                <div className={classes.warningBanner}>
                  <span className={classes.warningIcon}>⚠️</span>
                  <div className={classes.warningText}>
                    <strong>Dica:</strong> Preencha as 5 mensagens para evitar bloqueios.
                  </div>
                </div>
                {previewMessages.message1 || previewMessages.message2 || previewMessages.message3 || previewMessages.message4 || previewMessages.message5 ? (
                  <>
                    {previewMessages.message1 && (
                      <div className={`${classes.messageBubble} ${classes.messageBubbleReceived}`}>
                        {previewMessages.message1}
                      </div>
                    )}
                    {previewMessages.message2 && (
                      <div className={`${classes.messageBubble} ${classes.messageBubbleReceived}`}>
                        {previewMessages.message2}
                      </div>
                    )}
                    {previewMessages.message3 && (
                      <div className={`${classes.messageBubble} ${classes.messageBubbleReceived}`}>
                        {previewMessages.message3}
                      </div>
                    )}
                    {previewMessages.message4 && (
                      <div className={`${classes.messageBubble} ${classes.messageBubbleReceived}`}>
                        {previewMessages.message4}
                      </div>
                    )}
                    {previewMessages.message5 && (
                      <div className={`${classes.messageBubble} ${classes.messageBubbleReceived}`}>
                        {previewMessages.message5}
                      </div>
                    )}
                  </>
                ) : (
                  <div className={classes.emptyPhonePreview}>
                    Digite as mensagens para ver a prévia aqui
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignConfig;
